import React, { useRef, useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import { EmtTypography, EmtButton } from 'emt-components-v2'
import { useSignUpCallRequestStore } from '@/store/signUpCallRequestStore'
import useIsResponsive from '@/hooks/useIsResponsive'
import { scrollToFirstError } from '@/utils'

import AdditionalInformationsForm, { AdditionalInformationsValues } from './AdditionalInformationsForm'

import type { AdditionalInformationsHandle } from '@/components/pages/pet-sitter/reservations/NewRequest/AdditionalInformations'

interface AdditionalInformationsStepComponentProps {
  onNext: () => void
  topContent?: React.ReactNode
  submitLabel?: string
  loading?: boolean
  createMission?: boolean
}

const AdditionalInformationsStep: React.FC<AdditionalInformationsStepComponentProps> = ({
  onNext,
  topContent,
  submitLabel = 'Continuer',
  loading = false,
  createMission = false,
}) => {
  const [isMobile] = useIsResponsive()

  const pets = useSignUpCallRequestStore((s) => s.animals)
  const service = useSignUpCallRequestStore((s) => s.service)

  const message = useSignUpCallRequestStore((s) => s.message)
  const setMessage = useSignUpCallRequestStore((s) => s.setMessage)

  const additionalInformations = useSignUpCallRequestStore((s) => s.additionalInformations)
  const setAdditionalInformations = useSignUpCallRequestStore((s) => s.setAdditionalInformations)

  const [localAdditionalInformations, setLocalAdditionalInformations] =
    useState<AdditionalInformationsValues>(additionalInformations)

  const [localDescription, setLocalDescription] = useState(message || '')

  const additionalInfosRef = useRef<AdditionalInformationsHandle | null>(null)

  const canContinue = pets.length > 0
  const hasSelectedDog = pets.some((pet) => pet.selectedRadio === 'dog')

  const handleContinue = () => {
    const ok = additionalInfosRef.current?.validate?.()
    if (ok === false) {
      scrollToFirstError()
      return
    }

    setAdditionalInformations(localAdditionalInformations)
    setMessage(localDescription)
    onNext()
  }

  useEffect(() => {
    setLocalAdditionalInformations(additionalInformations)
  }, [additionalInformations])

  useEffect(() => {
    setLocalDescription(message || '')
  }, [message])

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
      <Box>
        <EmtTypography variant="h3" fontWeight="800" sx={{ fontSize: isMobile ? '24px' : '34px' }}>
          {createMission ? 'Finalisation de votre demande' : 'A propos de vos animaux'}
        </EmtTypography>
      </Box>

      <EmtTypography variant="body1" fontWeight="700">
        {createMission
          ? 'Ajoutez les animaux à faire garder ainsi que leurs caractéristiques, cela nous permettra de trouver les pet sitters parfaits :'
          : 'Ces informations sont importantes pour vous trouver le pet sitter parfait pour vos animaux :'}
      </EmtTypography>

      <AdditionalInformationsForm
        topContent={topContent}
        isMobile={isMobile}
        service={service?.selectedService || ''}
        hasSelectedDog={hasSelectedDog}
        values={localAdditionalInformations}
        setValues={setLocalAdditionalInformations}
        description={localDescription}
        setDescription={setLocalDescription}
        additionalInfosRef={additionalInfosRef}
      />

      <EmtButton
        fullWidth
        color="purple"
        size="large"
        variant="contained"
        disabled={!canContinue || loading}
        loader={loading}
        onClick={handleContinue}
      >
        {submitLabel}
      </EmtButton>
    </Box>
  )
}

export default AdditionalInformationsStep
